/* eslint-disable react-hooks/exhaustive-deps */
import React, { useRef, useMemo, useEffect, useState, useCallback } from 'react';
import useMount from 'react-use/lib/useMount';
import useToggle from 'react-use/lib/useToggle';
import { useSelector } from 'react-redux';
import { RouteComponentProps, Redirect, Link } from 'react-router-dom';
import _ from 'lodash';
import { Mask, usePageNav, DashboardToggle, getClassNames, newId } from '@tradingblock/components';
import { DashboardDrawer } from '../../components/dashboard/DashboardDrawer';
import { DashboardMenu } from '../../components/dashboard/Menu';
import { DashboardNavigation } from '../../components/dashboard/DashboardNavigation';
import { DashboardContentWrapper } from '../../components/dashboard/DashboardContentWrapper';
import { DashboardWrapper } from '../../components/dashboard/DashboardWrapper';
import { useStateSelector, apiTokenSelector, dashboardsSelectors } from '../../data/global/dataSelectors';
import { useDispatcher } from '../../data/global/hooks';
import { useDashboardDrawer } from '../../hooks/useDashboardDrawer';
import { getCreateDashboardRequest } from '../../utilities/dashboard';
import { tryCreateDashboard } from '../../data/async/dashboards';
import { useDefaultDashboards } from '../../components/dashboard/hooks/useDefaultDashboards';
import { useStore } from '../../context/Storage';
import { usePageSettings } from '../../framework/WrappedRoute';
import Config, { buildLegacyTradingBlockEmbedUrl } from '../../config';

interface LegacyTradingBlockRouteParams {
  dashboardid: string;
}

export const LegacyTradingBlockView: React.FC<RouteComponentProps<LegacyTradingBlockRouteParams>> = ({
  match,
}) => {
  usePageSettings({ name: 'Legacy TradingBlock', access: 'public' });

  const [menuOpen, toggleMenu] = useToggle(false);
  const [nav] = usePageNav();
  const [drawerIsOpen, setDrawerIsOpen, toggleDrawer] = useDashboardDrawer();
  const menuRef = useRef(null);

  const dashboardIdFromUrl = match.params.dashboardid;
  const token = useStateSelector(s => s.auth.uiApiToken);
  const apiToken = useStateSelector(apiTokenSelector);
  const dashboards = useSelector(dashboardsSelectors.dashboards);
  const dashboard = useSelector(dashboardsSelectors.dashboard);

  const { dispatcher } = useDispatcher();
  const store = useStore();

  const [defaultDashboards] = useDefaultDashboards();
  const [addingDefaultDashboard, setAddingDefaultDashboard] = useState(false);

  const redirectToDashboardId = useMemo(() => {
    return dashboard && dashboard.dashboardId;
  }, [dashboard]);

  useMount(() => {
    if (token) {
      dispatcher.dashboards.requestDashboards();
      dispatcher.favorites.request();
    }
  });

  useEffect(() => {
    if (dashboards && !dashboard) {
      if (_.isEmpty(dashboards)) {
        const firstDefaultDashboard = _.first(defaultDashboards);
        if (firstDefaultDashboard && !addingDefaultDashboard) {
          const newBoard = {
            ...firstDefaultDashboard,
            dashboardId: newId(),
          };
          const createRequest = getCreateDashboardRequest(newBoard);

          setAddingDefaultDashboard(true);
          tryCreateDashboard(store, createRequest).then(dashboardSaved => {
            if (!dashboardSaved) {
              console.warn('[LegacyTradingBlockView] saving default dashboard failed', newBoard);
            }
            dispatcher.dashboards.setDashboard({ dashboard: { ...newBoard } });
            setAddingDefaultDashboard(false);
          });
        }
      } else {
        const next = _.find(dashboards, d => d.dashboardId === dashboardIdFromUrl) || dashboards[0];
        dispatcher.dashboards.setDashboard({ dashboard: next });
      }
    }
  }, [
    dashboards,
    dashboard,
    defaultDashboards,
    dashboardIdFromUrl,
    addingDefaultDashboard,
    store,
    dispatcher.dashboards,
  ]);

  useEffect(() => {
    if (dashboard && dashboard.dashboardId === dashboardIdFromUrl) {
      dispatcher.block.requestBlocks();
    }
  }, [dashboard, dashboardIdFromUrl, dispatcher.grid]);

  const dashboardClass = useMemo(() => {
    return getClassNames([
      drawerIsOpen ? 'dashboard-drawer-open' : undefined,
      menuOpen ? 'dashboard-menu-open' : undefined,
      nav.collapsed ? 'collapsed-nav' : undefined,
    ]);
  }, [drawerIsOpen, menuOpen, nav.collapsed]);

  /** RestHub API token: `state.auth.apiToken` (JWT `token` claim, set on `receiveToken`). */
  const legacyIframeSrc = useMemo(() => buildLegacyTradingBlockEmbedUrl(apiToken), [apiToken]);

  /**
   * Set iframe `src` in an effect (not on first render) so the legacy URL always includes `AuthGuid`
   * and the browser never starts a navigation before Redux has `apiToken`.
   * New-tab works more often than iframe when the parent origin is cross-site (e.g. localhost → legacystaging),
   * because session cookies can be blocked in embedded third-party contexts.
   */
  const legacyIframeRef = useRef<HTMLIFrameElement | null>(null);
  useEffect(() => {
    const el = legacyIframeRef.current;
    if (!el) {
      return;
    }
    if (!apiToken) {
      el.src = 'about:blank';
      return;
    }
    console.log('[LegacyTradingBlockView] assigning legacy iframe src (AuthGuid SSO)');
    el.src = legacyIframeSrc;
  }, [apiToken, legacyIframeSrc]);

  const handleLegacyIframeLoad = useCallback(() => {
    console.log('[LegacyTradingBlockView] legacy iframe load event', {
      hasLegacySsoParams: Boolean(apiToken),
    });
  }, [apiToken]);

  if (!Config.showLegacyTradingBlockEmbed) {
    console.log(
      '[LegacyTradingBlockView] legacy embed not available in this environment (dev/local only); redirecting to dashboard'
    );
    return <Redirect to={`/${dashboardIdFromUrl}`} />;
  }

  if (redirectToDashboardId && redirectToDashboardId !== dashboardIdFromUrl) {
    return <Redirect to={`/${redirectToDashboardId}/legacy-tradingblock`} />;
  }

  return (
    <DashboardWrapper className={dashboardClass}>
      <Mask />

      <DashboardNavigation
        toggleDrawer={toggleDrawer}
        drawerIsOpen={drawerIsOpen}
        menuRef={menuRef}
        menuOpen={menuOpen}
        toggleMenu={toggleMenu}
      />

      <DashboardContentWrapper
        className="legacy-tradingblock-dashboard-content"
        editClass="edit-mode"
        linkingClass="link-mode"
      >
        <div
          className="legacy-tradingblock-embed"
          style={{
            width: '100%',
            height: 'calc(100vh - var(--dashboard-nav-height, 56px) - var(--gap, 16px))',
            minHeight: 320,
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: '#ffffff',
          }}
        >
          <div className="legacy-tradingblock-embed-toolbar">
            <Link to={`/${dashboardIdFromUrl}`} className="legacy-tradingblock-back-link">
              <i className="fal fa-arrow-left" aria-hidden="true" /> Back to dashboard
            </Link>
            {apiToken ? (
              <a
                href={legacyIframeSrc}
                target="_blank"
                rel="noopener noreferrer"
                className="legacy-tradingblock-back-link"
                style={{ marginLeft: '1rem' }}
              >
                <i className="fal fa-external-link" aria-hidden="true" /> Open legacy in new tab
              </a>
            ) : null}
            {typeof window !== 'undefined' &&
            (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') ? (
              <span
                className="legacy-tradingblock-back-link"
                style={{ marginLeft: '1rem', opacity: 0.85, fontSize: '0.875rem' }}
                title="Browsers often block session cookies for sites embedded in an iframe across different origins. Use Open in new tab on localhost."
              >
                Embedded legacy may not keep login on localhost — use &quot;Open in new tab&quot; if needed.
              </span>
            ) : null}
          </div>
          <div
            style={{
              flex: 1,
              minHeight: 0,
              position: 'relative',
            }}
          >
            <iframe
              ref={legacyIframeRef}
              title="Legacy TradingBlock"
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                border: 0,
                backgroundColor: '#ffffff',
              }}
              allow="fullscreen"
              onLoad={handleLegacyIframeLoad}
            />
          </div>
        </div>
        {!drawerIsOpen && <DashboardToggle />}
      </DashboardContentWrapper>

      <DashboardDrawer isOpen={drawerIsOpen} toggleIsOpen={() => setDrawerIsOpen(false)} />

      <div className="dashboard-menu" ref={menuRef}>
        <DashboardMenu toggleMenu={toggleMenu} />
      </div>
    </DashboardWrapper>
  );
};
